import { useState, useRef, useEffect, useCallback } from 'react';
import { timerSystem, createTimerObject } from '../utils';

interface TimerCoreOptions {
  onTimerUpdate?: (time: number) => void;
  autoStart?: boolean;
}

interface TimerCoreState {
  time: number;
  isRunning: boolean;
  startTimer: () => void;
  startNewTimer: () => void;
  pauseTimer: () => void;
  resetTimer: () => void;
  resetAndStart: () => void;
  updateTimerValue: () => void;
}

/**
 * Custom hook for core timer functionality
 * Handles timer start, pause, and reset operations using a shared animation frame system
 *
 * @param options - Configuration options for the timer
 * @returns Timer state and control methods
 */
const useTimerCore = ({
  onTimerUpdate,
  autoStart = false,
}: TimerCoreOptions = {}): TimerCoreState => {
  // Timer states
  const [time, setTime] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(autoStart);

  // Refs to track time across renders
  const timeRef = useRef<number>(0); // Accumulated time before current start
  const startTimestampRef = useRef<number | null>(autoStart ? Date.now() : null); // When the timer was started
  const lastTimeRef = useRef<number>(0); // Keep track of the last known time

  // Create a timer object for the shared system
  const timerRef = useRef(
    createTimerObject({
      startTimestampRef,
      timeRef,
      onTimerUpdate: (elapsed: number) => {
        setTime(elapsed);
        if (onTimerUpdate) {
          onTimerUpdate(elapsed);
        }
      },
    })
  ).current;

  // Update time ref whenever state value changes
  useEffect(() => {
    lastTimeRef.current = time;
  }, [time]);

  // Effect to handle the timing logic
  useEffect(() => {
    if (isRunning) {
      // Initialize timestamp ref if it's not set
      if (!startTimestampRef.current) {
        startTimestampRef.current = Date.now();
      }

      // Add this timer to the shared system
      timerSystem.addTimer(timerRef);

      return () => {
        // Remove this timer from the shared system when unmounted or not running
        timerSystem.removeTimer(timerRef);
      };
    } else {
      // Not running, ensure we're removed from the shared system
      timerSystem.removeTimer(timerRef);
    }
  }, [isRunning, timerRef]);

  // Manual update function for external synchronization
  const updateTimerValue = useCallback(() => {
    if (isRunning && startTimestampRef.current) {
      const elapsed = Date.now() - startTimestampRef.current + timeRef.current;
      setTime(elapsed);
      if (onTimerUpdate) {
        onTimerUpdate(elapsed);
      }
    }
  }, [isRunning, onTimerUpdate]);

  /**
   * Start the timer
   */
  const startTimer = useCallback(() => {
    // Only start if not already running
    if (!isRunning) {
      // Set current timestamp as the start time
      startTimestampRef.current = Date.now();

      // Start the timer
      setIsRunning(true);
    }
  }, [isRunning]);

  /**
   * Start a new timer session
   */
  const startNewTimer = useCallback(() => {
    // Reset accumulated time
    timeRef.current = 0;
    // Set current timestamp as the start time
    startTimestampRef.current = Date.now();
    // Start the timer
    setIsRunning(true);
  }, []);

  /**
   * Pause the timer
   */
  const pauseTimer = useCallback(() => {
    if (isRunning) {
      // Store the current time value before pausing
      timeRef.current = time;

      // Clear timestamp
      startTimestampRef.current = null;

      // Stop the timer
      setIsRunning(false);
    }
  }, [time, isRunning]);

  /**
   * Reset the timer
   */
  const resetTimer = useCallback(() => {
    // First ensure timer is stopped
    setIsRunning(false);

    // Reset all values
    setTime(0);
    timeRef.current = 0;
    startTimestampRef.current = null;
    lastTimeRef.current = 0;
  }, []);

  /**
   * Reset and start timer in one operation
   */
  const resetAndStart = useCallback(() => {
    // First reset
    resetTimer();

    // Use setTimeout to ensure state updates have been processed
    // before starting the timer again
    setTimeout(() => {
      startNewTimer();
    }, 0);
  }, [resetTimer, startNewTimer]);

  return {
    time,
    isRunning,
    startTimer,
    startNewTimer,
    pauseTimer,
    resetTimer,
    resetAndStart,
    updateTimerValue,
  };
};

export default useTimerCore;
